/* eslint-disable */
/* eslint-disable eslint-comments/no-unused-disable */
/* 
* The file is automatically generated by Serenity UI.
* 
* ⚠️ WARNING: Any manual changes may be overwritten during the next upgrade
* 
* Found an issue? Submit a task to the ClickUp board:
* https://app.clickup.com/1255586/v/l/6-901510012553-1?pr=90154030902
*/
import * as stylex from '@stylexjs/stylex';
import { type ForwardedRef, createContext, forwardRef } from 'react';
import { type ContextValue, ProgressBar as ProgressBarCore, type ProgressBarProps as ProgressBarCoreProps, useContextProps } from 'react-aria-components';
import { $ } from '../../tokens.stylex';

interface ProgressIndicatorProps extends Omit<ProgressBarCoreProps, 'style' | 'className' | 'children' | 'isIndeterminate'> {
  /** The size variant of the progress indicator. (Default: 16) */
  size?: keyof typeof sizeVariants;
  /** The variant of the progress indicator. (Default: primary) */
  variant?: keyof typeof indicatorVariantVariants;
  style?: stylex.StyleXStyles;
}

const ProgressIndicatorContext = createContext<ContextValue<ProgressIndicatorProps, HTMLDivElement>>({});

const ProgressIndicator = forwardRef((props: ProgressIndicatorProps, ref: ForwardedRef<HTMLDivElement>) => {
  [props, ref] = useContextProps(props, ref, ProgressIndicatorContext);

  const { style, size = 16, variant = 'primary', value, ...coreProps } = props;
  const isIndeterminate = value == null;
  const normalizedValue = isIndeterminate ? 0 : Math.min(100, Math.max(0, value));

  const center = size / 2;
  const radius = center - STROKE_WIDTH / 2;
  const circumference = 2 * radius * Math.PI;

  return (
    <ProgressBarCore
      ref={ref}
      aria-label="Loading..."
      isIndeterminate={isIndeterminate}
      value={value}
      {...stylex.props(styles.base, sizeVariants[size], style)}
      {...coreProps}>
      <svg
        viewBox={`0 0 ${size} ${size}`}
        {...stylex.props(spinnerStyles.base, isIndeterminate && spinnerStyles.indeterminate)}>
        <circle
          cx={center}
          cy={center}
          r={radius}
          {...stylex.props(trackStyles.base, trackVariantVariants[variant])}
        />
        <circle
          cx={center}
          cy={center}
          r={radius}
          {...stylex.props(
            indicatorStyles.base({ center, circumference, isIndeterminate, value: normalizedValue }),
            indicatorVariantVariants[variant]
          )}
        />
      </svg>
    </ProgressBarCore>
  );
});

const STROKE_WIDTH = 2;

const styles = stylex.create({
  base: {
    alignItems: 'center',
    display: 'inline-flex',
    flexShrink: 0,
    justifyContent: 'center',
    position: 'relative',
  },
});

const sizeVariants = stylex.create({
  8: {
    height: 8,
    width: 8,
  },
  12: {
    height: 12,
    width: 12,
  },
  16: {
    height: 16,
    width: 16,
  },
  20: {
    height: 20,
    width: 20,
  },
  24: {
    height: 24,
    width: 24,
  },
  32: {
    height: 32,
    width: 32,
  },
  48: {
    height: 48,
    width: 48,
  },
});

const spin = stylex.keyframes({
  from: {
    transform: 'rotate(-90deg)',
  },
  to: {
    transform: 'rotate(270deg)',
  },
});

const spinnerStyles = stylex.create({
  base: {
    fill: 'none',
    strokeWidth: STROKE_WIDTH,
    transform: 'rotate(-90deg)',
    transformOrigin: 'center',
    height: '100%',
    width: '100%',
  },
  indeterminate: {
    animationDuration: '0.5s',
    animationIterationCount: 'infinite',
    animationName: spin,
    animationTimingFunction: 'linear',
  }
});

const trackStyles = stylex.create({
  base: {
    fill: 'none',
    stroke: $.border08,
  },
});

const trackVariantVariants = stylex.create({
  primary: {
    stroke: $.border08,
  },
  interactive: {
    stroke: $.borderInteractiveSilent,
  },
  inverted: {
    stroke: $.opacity12Inverted,
  },
});

const indicatorStyles = stylex.create({
  base: (props: { center: number, circumference: number, isIndeterminate: boolean, value: number }) => ({
    fill: 'none',
    strokeDasharray: `${props.circumference} ${props.circumference}`,
    strokeDashoffset: props.isIndeterminate
      ? props.circumference * 0.8
      : props.circumference * (1 - props.value / 100),
    strokeLinecap: 'round',
    transition: 'stroke-dashoffset 0.3s ease',
  })
});

const indicatorVariantVariants = stylex.create({
  primary: {
    stroke: $.iconPrimary,
  },
  interactive: {
    stroke: $.iconInteractive,
  },
  inverted: {
    stroke: $.iconInverted,
  },
});

const PROGRESS_INDICATOR_MIN_DELAY_DURATION = 300;

export { PROGRESS_INDICATOR_MIN_DELAY_DURATION, ProgressIndicator, ProgressIndicatorContext, type ProgressIndicatorProps };
